import { Component, Input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { STATUS_OPTIONS, ReportableEntityType, StatusOption } from '../social-section/social-section.component';

/**
 * Interface for a single report in the recent reports list
 * Matches actual backend response shape
 */
export interface RecentReport {
  id: string;
  reportTypes: string[];
  category?: string | null;
  note?: string | null;
  photoUrl?: string | null;
  upvotes: number;
  downvotes: number;
  denials?: number;
  createdAt: string;
  expiresAt?: string | null;
  userId?: string;
  userName?: string;
}

/**
 * RecentReportsComponent - Displays recent user reports for a point/container
 * 
 * Shows a compact list of recent status reports with:
 * - Report type emoji and label
 * - Username who reported (or "Anónimo")
 * - Time since report
 * - Confirmation/denial counts
 */
@Component({
  selector: 'app-recent-reports',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './recent-reports.component.html',
  styleUrl: './recent-reports.component.scss'
})
export class RecentReportsComponent {
  /** Array of recent reports to display */
  @Input() set reports(value: RecentReport[] | null | undefined) {
    this._reports.set(value ?? []);
  }
  
  /** Maximum number of reports to show initially */
  @Input() maxVisible: number = 3;
  
  /** Color theme variant */
  @Input() colorVariant: 'primary' | 'secondary' | 'tertiary' | 'cuaternary' = 'secondary';

  /** Entity type to resolve status icons/labels */
  @Input() entityType: ReportableEntityType = 'container';

  private _reports = signal<RecentReport[]>([]);
  
  /** Public getter for total reports count (used in template) */
  totalReports = computed(() => this._reports().length);
  
  /** Whether to show all reports or just maxVisible */
  showAll = signal<boolean>(false);

  /** Computed: reports to display based on showAll state */
  visibleReports = computed(() => {
    const reports = this._reports();
    if (this.showAll() || reports.length <= this.maxVisible) {
      return reports;
    }
    return reports.slice(0, this.maxVisible);
  });

  /** Computed: whether there are more reports to show */
  hasMoreReports = computed(() => {
    return this._reports().length > this.maxVisible;
  });

  /** Computed: number of hidden reports */
  hiddenCount = computed(() => {
    return Math.max(0, this._reports().length - this.maxVisible);
  });

  /** Computed: check if there are any reports */
  hasReports = computed(() => {
    return this._reports().length > 0;
  });

  /** Toggle showing all reports */
  toggleShowAll(): void {
    this.showAll.update(v => !v);
  }

  /** Resolve a single report type string to its StatusOption */
  private getStatusOption(reportType: string): StatusOption | undefined {
    const options = STATUS_OPTIONS[this.entityType] ?? [];
    return options.find(o => o.id === reportType);
  }

  /** Get the icon src for a report type */
  getReportTypeIcon(reportType: string): string {
    return this.getStatusOption(reportType)?.icon || 'assets/icons/exclamation.svg';
  }

  /** Get the label for a report type */
  getReportTypeLabel(reportType: string): string {
    return this.getStatusOption(reportType)?.label || reportType;
  }

  /** Format the time since the report was made */
  getTimeAgo(report: RecentReport): string {
    if (!report.createdAt) return '';
    
    const reportDate = new Date(report.createdAt);
    const now = new Date();
    const diffMs = now.getTime() - reportDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'ahora';
    if (diffMins < 60) return `hace ${diffMins}m`;
    if (diffHours < 24) return `hace ${diffHours}h`;
    if (diffDays < 7) return `hace ${diffDays}d`;
    if (diffDays < 30) return `hace ${Math.floor(diffDays / 7)}sem`;
    return `hace ${Math.floor(diffDays / 30)}mes`;
  }

  /** Get net votes (upvotes - downvotes) */
  getNetVotes(report: RecentReport): number {
    return (report.upvotes || 0) - (report.downvotes || 0);
  }

  /** Get vote display class based on net votes */
  getVoteClass(report: RecentReport): string {
    const net = this.getNetVotes(report);
    if (net > 0) return 'positive';
    if (net < 0) return 'negative';
    return 'neutral';
  }
}
